/* ChaptersPage — v10.6 redesign.
 *
 *  Renders one card per year with:
 *   • navy square + BookOpen icon on the left
 *   • Playfair "Chapter {year}" + "N Stories" + one-liner description
 *   • cover image on the right (user upload OR most-recent story photo
 *     OR "Add cover photo" cream placeholder)
 *   • navy circular arrow → /chapters/{year}
 *  Plus a filter dropdown, a "+ new chapter" pill, and a friendly empty
 *  state. Auto-sync of completed booked trips into their year (Phase 3)
 *  is untouched — this page just reads the new by_year shape returned
 *  by the backend.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, ChevronRight, ChevronDown, Plus, ImagePlus, Sparkles,
  Star, Camera,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SectionRibbon } from "@/components/AppShell";
import { toast } from "sonner";

const CURRENT_YEAR = new Date().getFullYear();

const TIDS = {
  page: "chapters-page",
  card: (year) => `chapter-card-${year}`,
  filter: "chapters-year-filter",
  newBtn: "chapters-new-btn",
  changeCover: (year) => `chapters-change-cover-${year}`,
  empty: "chapters-empty-state",
};

function describeChapter(count, year, isSynthetic) {
  if (isSynthetic) return `Start your ${year} chapter \u2192`;
  if (count === 0) return "This chapter is waiting for your first story.";
  if (count === 1) return "A quiet adventure preserved in one story.";
  if (count <= 3) return `${count} adventures worth remembering.`;
  return `${count} adventures preserved this year.`;
}

async function promptUploadCover(year, onDone) {
  const url = window.prompt(
    "Paste an image URL for this chapter's cover photo (or leave blank to reset):",
    ""
  );
  if (url === null) return;
  try {
    await api.patch(`/chapters/year/${year}`, { cover_image: url.trim() });
    toast.success(url.trim() ? "Cover updated" : "Cover reset");
    onDone && onDone();
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save cover");
  }
}

function ChapterCard({ y, onOpen, onCoverChanged }) {
  const [hover, setHover] = useState(false);
  const count = (y.journeys || []).length;
  const hasCover = !!(y.cover_image && y.cover_image.trim());
  return (
    <div
      data-testid={TIDS.card(y.year)}
      className="rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition p-4 md:p-5 flex items-center gap-3 md:gap-6 cursor-pointer group"
      onClick={onOpen}
    >
      {/* v10.6.1 — Mobile: shrink the navy book square and drop the
          right-side chevron button so the middle text has room to
          breathe. Desktop keeps the mockup exactly as shipped. */}
      <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-tb-navy-deep flex items-center justify-center shrink-0">
        <BookOpen className="w-6 h-6 md:w-8 md:h-8 text-tb-gold-bright" strokeWidth={1.6} />
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="font-serif-it italic text-tb-navy text-xl md:text-3xl leading-tight">
          Chapter {y.year}
        </h3>
        <p className="mt-0.5 text-sm md:text-base text-slate-600 font-medium">
          {count} {count === 1 ? "Story" : "Stories"}
        </p>
        <p className="mt-0.5 text-xs md:text-base italic text-slate-500 line-clamp-2">
          {describeChapter(count, y.year, !!y.is_synthetic)}
        </p>
      </div>

      {/* Cover thumbnail. On mobile: fixed w-24 h-24 rounded square so
          the title has room. On desktop: wider landscape thumbnail per
          mockup. Priority: user upload > most-recent story photo >
          "Add cover photo" placeholder. */}
      <div
        className="relative shrink-0 w-24 h-24 md:w-40 md:h-24 lg:w-48 lg:h-28 rounded-xl overflow-hidden"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {hasCover ? (
          <img
            src={y.cover_image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <button
            type="button"
            data-testid={`chapters-add-cover-${y.year}`}
            onClick={(e) => { e.stopPropagation(); promptUploadCover(y.year, onCoverChanged); }}
            className="absolute inset-0 w-full h-full bg-tb-cream border border-tb-cream-3 flex flex-col items-center justify-center gap-1 hover:bg-tb-cream-2 transition-colors p-1"
          >
            <ImagePlus className="w-5 h-5 md:w-6 md:h-6 text-tb-navy/50" strokeWidth={1.6} />
            <span className="font-serif-it italic text-tb-navy/60 text-[10px] md:text-sm leading-tight text-center">
              Add cover photo
            </span>
          </button>
        )}
        {hasCover && hover && (
          <button
            type="button"
            data-testid={TIDS.changeCover(y.year)}
            onClick={(e) => { e.stopPropagation(); promptUploadCover(y.year, onCoverChanged); }}
            className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 border border-tb-gold/60 text-tb-navy text-xs font-hand shadow-sm"
          >
            <Camera className="w-3 h-3" />
            Change photo
          </button>
        )}
      </div>

      {/* Chevron arrow — desktop only. Mobile taps anywhere on the card
          to navigate, so the chevron just adds clutter. */}
      <button
        type="button"
        aria-label={`Open Chapter ${y.year}`}
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        className="hidden md:flex w-11 h-11 rounded-full bg-tb-navy-deep text-white items-center justify-center shrink-0 hover:brightness-110 tb-tap"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

export default function ChaptersPage() {
  const nav = useNavigate();
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newYear, setNewYear] = useState(CURRENT_YEAR);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chapters");
      setYears(data.by_year || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load chapters");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Autopopulate the last 5 years (current + 4 prior) descending. Any
  // year the user already has stories in wins over the synthetic
  // placeholder. Older user chapters (e.g. 2019) still render below.
  const yearsWithAutopop = useMemo(() => {
    const map = new Map();
    for (const y of years) map.set(y.year, y);
    for (let yr = CURRENT_YEAR; yr >= CURRENT_YEAR - 4; yr--) {
      if (!map.has(yr)) {
        map.set(yr, { year: yr, journeys: [], cover_image: "", is_synthetic: true });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.year - a.year);
  }, [years]);

  const filtered = useMemo(
    () => (filter === "all"
      ? yearsWithAutopop
      : yearsWithAutopop.filter((y) => String(y.year) === String(filter))),
    [yearsWithAutopop, filter]
  );

  const createChapter = async () => {
    try {
      await api.post("/chapters", {
        year: Number(newYear),
        trip_name: `Chapter ${newYear}`,
        country: "",
        code: "",
        icon: "plane",
        stamp_color: "#1a3a5f",
        start_date: `${newYear}-01-01`,
        end_date: `${newYear}-12-31`,
        rating: 5,
        image: "",
        favorite_moments: [],
        photos: [],
      });
      setShowNew(false);
      toast.success(`Chapter ${newYear} started`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create chapter");
    }
  };

  return (
    <div data-testid={TIDS.page} className="flex flex-col bg-white">
      <SectionRibbon>your chapters</SectionRibbon>
      <span data-tour="chapters-header" className="block" style={{position: "absolute", pointerEvents: "none", height: 1, width: 1}} aria-hidden="true" />

      <div className="px-6 md:px-10 lg:px-12 pt-6 pb-10">
        {/* Header row: title + filter + new-chapter */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-tb-gold" fill="currentColor" strokeWidth={1.4} />
            <h1 className="font-serif-it italic text-tb-gold text-2xl md:text-3xl">your chapters</h1>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <button
                data-testid={TIDS.filter}
                onClick={() => setShowYearPicker((s) => !s)}
                className={
                  "flex items-center gap-1.5 rounded-full bg-white border px-4 py-2 text-sm text-tb-navy hover:border-tb-gold transition tb-tap " +
                  (filter === "all" ? "border-tb-gold/40" : "border-tb-gold-bright text-tb-gold-soft font-semibold")
                }
              >
                {filter === "all" ? "Filter by year" : `Chapter ${filter}`}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showYearPicker && (
                <ul className="absolute right-0 mt-2 z-10 rounded-xl bg-white border border-slate-200 shadow-lg py-1 min-w-[10rem] max-h-60 overflow-auto">
                  {years.map((y) => (
                    <li key={y.year}>
                      <button
                        onClick={() => { setFilter(String(y.year)); setShowYearPicker(false); }}
                        className={"w-full text-left px-4 py-2 text-sm hover:bg-tb-cream " + (String(filter) === String(y.year) ? "text-tb-gold-soft font-semibold" : "text-tb-navy")}
                      >
                        Chapter {y.year}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {filter !== "all" && (
              <button
                type="button"
                data-testid="chapters-clear-filter"
                onClick={() => setFilter("all")}
                className="font-hand text-xs text-tb-navy/60 hover:text-tb-coral underline underline-offset-2 tb-tap"
                aria-label="Clear year filter"
              >
                Clear filter
              </button>
            )}

            <button
              data-testid={TIDS.newBtn}
              onClick={() => setShowNew(true)}
              className="flex items-center gap-1.5 rounded-full bg-white border-2 border-tb-gold px-4 py-2 text-sm text-tb-navy font-semibold hover:bg-tb-gold/10 transition tb-tap"
            >
              <Plus className="w-4 h-4" /> new chapter
            </button>
          </div>
        </div>

        <p className="mt-2 font-serif-it italic text-slate-600 text-base md:text-lg">
          one chapter per year — filled with stories from your adventures!
        </p>

        <div className="mt-6">
          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-32 rounded-2xl bg-tb-cream-2 border border-tb-cream-3 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid={TIDS.empty} className="rounded-2xl bg-tb-cream border-2 border-tb-cream-3 py-14 px-6 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-tb-navy-deep flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-tb-gold-bright" strokeWidth={1.8} />
              </div>
              <p className="mt-4 font-serif-it italic text-tb-navy text-2xl md:text-3xl">
                No chapters yet
              </p>
              <p className="mt-2 font-hand text-tb-navy/60 text-sm md:text-base max-w-md mx-auto">
                Your booked trips will automatically become chapter entries once they end
                — or you can create a chapter manually.
              </p>
              <button
                onClick={() => setShowNew(true)}
                className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap hover:brightness-110"
              >
                <Plus className="w-4 h-4" /> new chapter
              </button>
            </div>
          ) : (
            <ul className="space-y-4 md:space-y-5">
              {filtered.map((y) => (
                <li key={y.year}>
                  <ChapterCard
                    y={y}
                    onOpen={() => nav(`/chapters/${y.year}`)}
                    onCoverChanged={load}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-4">
          <div className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full" role="dialog" aria-modal="true">
            <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <span className="font-hand text-xl">start a new chapter</span>
              <button onClick={() => setShowNew(false)} className="font-hand text-tb-gold tb-tap">close</button>
            </div>
            <div className="p-4 space-y-3">
              <label className="block">
                <span className="font-hand text-tb-navy/70 text-sm">Year</span>
                <input
                  type="number" min="1900" max="2999" value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-tb-cream-2 border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-serif-it text-tb-navy text-2xl"
                />
              </label>
              <button
                onClick={createChapter}
                className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap"
              >
                start chapter {newYear}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
